import { useResizeObserver } from "@/shared/base/hooks/useResizeObserver";
import React, {
  useRef,
  useState,
  ReactNode,
  useLayoutEffect,
  useEffect,
} from "react";

interface MarqueeTextV2Props {
  children: ReactNode;
  className?: string;
  speed?: number; // Pixels per second
  debug?: boolean;
  gap?: number;
  onMarquee?: (args: {
    shouldMarquee: boolean;
    container: HTMLDivElement;
    content: HTMLDivElement;
  }) => void;
}

export function MarqueeTextV2({
  children,
  className = "",
  speed = 50, // Default 50 pixels per second
  gap = 24,
  debug = false,
  onMarquee,
}: MarqueeTextV2Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const positionRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const [shouldMarquee, setShouldMarquee] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);

  const animate = (currentTime: number) => {
    if (!contentRef.current || !shouldMarquee) return;

    if (lastTimeRef.current !== 0) {
      const deltaTime = currentTime - lastTimeRef.current;
      const moveDistance = (speed * deltaTime) / 1000;
      positionRef.current -= moveDistance;
      const resetPoint = -(contentWidth + gap);
      if (positionRef.current <= resetPoint) positionRef.current = 0;
      contentRef.current.style.transform = `translateX(${positionRef.current}px)`;
    }

    lastTimeRef.current = currentTime;
    animationRef.current = requestAnimationFrame(animate);
  };

  const startAnimation = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    positionRef.current = 0;
    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
    if (contentRef.current) {
      contentRef.current.style.transform = "translateX(0px)";
    }
    positionRef.current = 0;
    lastTimeRef.current = 0;
  };

  const checkOverflow = async () => {
    if (containerRef.current && contentRef.current) {
      // Get the first child to measure the actual content width
      const firstChild = contentRef.current.children[0] as HTMLElement;
      if (firstChild) {
        const containerW = containerRef.current.offsetWidth;
        const contentW = firstChild.offsetWidth;
        const shouldMarquee = contentW > containerW;
        if (debug) {
          console.log("containerW", containerW);
          console.log("contentW", contentW);
          console.log("shouldMarquee", shouldMarquee);
        }
        setContentWidth(contentW);
        setShouldMarquee(shouldMarquee);
      }
    }
  };

  useLayoutEffect(() => {
    if (shouldMarquee) startAnimation();
    else stopAnimation();
    return () => stopAnimation();
  }, [shouldMarquee]);

  useResizeObserver({
    ref: containerRef,
    onResize: () => checkOverflow(),
  });

  useEffect(() => {
    if (!shouldMarquee && containerRef.current) {
      containerRef.current.style.width = "max-content";
    } else if (shouldMarquee && containerRef.current) {
      containerRef.current.style.width = "100%";
    }
  }, [shouldMarquee]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        ref={contentRef}
        style={{
          display: "flex",
          justifyContent: shouldMarquee ? "flex-start" : "center",
          whiteSpace: "nowrap",
          willChange: shouldMarquee ? "transform" : "auto",
        }}
      >
        {children}
        {shouldMarquee && (
          <>
            <span style={{ width: `${gap}px`, flexShrink: 0 }} />
            {children}
          </>
        )}
      </div>
    </div>
  );
}
